/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import Text from '../../basic/Text';
import { Title } from '../../basic/Title';
import { UnstyledButton } from '../../basic/UnstyledButton';

export interface ChoiceChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
  /** Overrides the default seed tint, e.g. a lease state's own colour. */
  accent?: { bg: string; color: string };
}

/**
 * One toggleable filter choice.
 *
 * `aria-pressed` rather than a checkbox role: these read as buttons that
 * stay pushed in, which is what they look like, and it is how the visit
 * and property panels behave.
 */
export function ChoiceChip({ label, active, onToggle, accent }: ChoiceChipProps) {
  const activeBg = accent?.bg ?? 'color-mix(in srgb, var(--seed-base) 8%, var(--surface-primary))';
  const activeColor = accent?.color ?? 'var(--seed-base)';

  return (
    <UnstyledButton
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      style={{
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-meta)',
        fontWeight: (active ? 'var(--font-semibold)' : 'var(--font-medium)') as unknown as number,
        fontFamily: 'var(--font-family)',
        cursor: 'pointer',
        transition: 'var(--transition-standard)',
        background: active ? activeBg : 'transparent',
        color: active ? activeColor : 'var(--text-secondary)',
        border: active
          ? `1.5px solid ${accent ? accent.color : 'color-mix(in srgb, var(--seed-base) 40%, transparent)'}`
          : '1px solid var(--border-subtle)',
      }}
    >
      {label}
    </UnstyledButton>
  );
}

export interface FilterSectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

/** A titled group of choices inside the panel body. */
export function FilterSection({ icon, title, children }: FilterSectionProps) {
  return (
    <div>
      <Title
        order={4}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          fontSize: 'var(--text-meta)',
          fontWeight: 'var(--font-bold)' as unknown as number,
          color: 'var(--text-primary)',
          margin: 0,
          marginBottom: 'var(--space-2)',
        }}
      >
        <Text as="span" style={{ color: 'var(--seed-base)', display: 'flex' }}>{icon}</Text>
        {title}
      </Title>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {children}
      </div>
    </div>
  );
}
